import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const LINKS = [
  { to: "/work", label: "Work", testId: "nav-work-link" },
  { to: "/custom", label: "Custom", testId: "nav-custom-link" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Freeze page scroll while the full-screen menu is open so the two never
  // compete; release it the moment the menu closes.
  useEffect(() => {
    if (open) {
      window.__lenis?.stop();
      document.body.style.overflow = "hidden";
    } else {
      window.__lenis?.start();
      document.body.style.overflow = "";
    }
    return () => {
      window.__lenis?.start();
      document.body.style.overflow = "";
    };
  }, [open]);

  // Any navigation (including browser back/forward) closes the menu.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
      {/* frosted separation band (sibling, painted below) so scrolling copy
          never collides with the brand; the header itself carries the blend
          so text is white-on-dark / black-on-light automatically */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-x-0 top-0 z-40 h-[72px] backdrop-blur-md"
      />
      <header
        data-testid="site-navbar"
        className="fixed inset-x-0 top-0 z-50 mix-blend-difference"
      >
        <div className="flex items-center justify-between px-5 py-4 md:px-10">
          <Link
            to="/"
            data-testid="nav-logo-link"
            className="font-display text-lg font-semibold tracking-[0.18em] uppercase transition-colors duration-300 hover:text-fog"
            onClick={() => setOpen(false)}
          >
            Findley&nbsp;St
          </Link>

          <nav className="hidden items-center gap-10 md:flex" aria-label="Primary">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testId}
                className={({ isActive }) =>
                  `group relative text-xs font-medium uppercase tracking-[0.25em] transition-colors duration-300 ${
                    isActive ? "text-white" : "text-fog hover:text-white"
                  }`
                }
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-white transition-[width] duration-500 ease-cinematic group-hover:w-full" />
              </NavLink>
            ))}
            <Link
              to="/start"
              data-testid="nav-start-link"
              className="group flex items-center gap-2 border border-white/20 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.25em] transition-colors duration-500 ease-cinematic hover:border-white hover:bg-white hover:text-obsidian"
            >
              Start a Project
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-500 ease-cinematic group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </nav>

          <button
            type="button"
            data-testid="nav-menu-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center border border-white/15 text-white md:hidden"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            key={location.pathname}
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="fixed inset-0 z-40 flex flex-col justify-end bg-obsidian/95 px-6 pb-16 backdrop-blur-xl md:hidden"
          >
            <nav className="flex flex-col gap-2" aria-label="Mobile">
              {[{ to: "/", label: "Home", testId: "mobile-nav-home-link" }, ...LINKS.map((l) => ({ ...l, testId: `mobile-${l.testId}` }))].map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.07, duration: 0.5, ease: EASE }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={l.testId}
                    onClick={() => setOpen(false)}
                    className="block border-b border-white/10 py-5 font-display text-4xl font-medium tracking-tight"
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, x: -24 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.32, duration: 0.5, ease: EASE }}
              >
                <Link
                  to="/start"
                  data-testid="mobile-nav-start-link"
                  onClick={() => setOpen(false)}
                  className="mt-8 flex items-center justify-between border border-white/25 px-6 py-5 font-display text-2xl font-medium tracking-tight"
                >
                  Start a Project
                  <ArrowUpRight className="h-6 w-6" />
                </Link>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
